import { RouterExitService } from './../../../routing/services/router-exit.service';
import { IDLENESS_TIME_TO_SHOW_WARNING, IDLENESS_WARNING, INACTIVITY_ERR_MESSAGE, SESSION_RENEWAL_CHECK_INTERVAL } from './../../constants/auth/auth.constant';
import { PortalConfig } from './../portal-config';
import { Title } from '@angular/platform-browser';
import { filter, take, tap, throttleTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { Auth } from '../../auth/auth.service';
import { SessionExpiredError } from '../../auth/session-expired-error';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';

@Injectable()
export class UserInactivity {
  private readonly defaultTitle: string;

  constructor (
    private router: Router,
    private auth: Auth,
    private idle: Idle,
    private title: Title,
    private portalConfig: PortalConfig,
    private routerExitService: RouterExitService,

  ) {
    this.defaultTitle = `${this.portalConfig.publicConfig.DISPLAY_NV_ENV} - Portal by New Visions`;
  }

  /**
   * Starts tracking once a session exists. After a login callback the SDK
   * reports the session only once the app has bootstrapped, so starting on a
   * one-time check would leave that page load, and the idle sign-out with it,
   * untracked.
   */
  public startWhenAuthenticated (): void {
    this.auth.isAuthenticated$.pipe(
      filter(isAuthenticated => isAuthenticated),
      take(1),
    ).subscribe(() => this.start());
  }

  public start (): void {
    this.idle.setIdle(IDLENESS_TIME_TO_SHOW_WARNING);
    this.idle.setTimeout(IDLENESS_WARNING);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe(() => {
      this.title.setTitle(this.defaultTitle);
      this.reset();
    });

    this.idle.onTimeoutWarning.subscribe((countdown: number) => {
      const idleState = `You will time out in ${countdown} seconds`;
      this.title.setTitle(idleState);
    });

    this.idle.onTimeout.pipe(
      tap(() => {
        this.title.setTitle(this.defaultTitle);
        this.onTimeout();
      }),
    ).subscribe();

    this.renewSessionWhileActive();

    this.idle.watch();
  }

  private reset (): void {
    this.idle.watch();
  }

  /**
   * Renewal otherwise happens only when a request asks for a token, so a user
   * working through a long stretch without one (writing a long note) could
   * outlast the provider's refresh-token inactivity lifetime and be signed out
   * mid-task. Activity is throttled to one check per interval; most resolve from
   * the SDK's cache, and the refresh token is exchanged once the access token
   * nears expiry. An idle user triggers nothing, so that lifetime still bounds a
   * session abandoned on an unmonitored device.
   */
  private renewSessionWhileActive (): void {
    this.idle.onInterrupt.pipe(
      throttleTime(SESSION_RENEWAL_CHECK_INTERVAL * 1000),
    ).subscribe(() => this.renewSession());
  }

  private async renewSession (): Promise<void> {
    try {
      await this.auth.getBearerToken();
    } catch (err) {
      // A lapsed session rejects only after Auth has already ended it.
      if (!(err instanceof SessionExpiredError)) throw err;
    }
  }

  private onTimeout (): void {
    const lastKnownUrl = this.router.url;
    const previousUrlValue = this.routerExitService?.previousUrl$?.value
    let prevUrl;

    if (previousUrlValue) {
      const { url, queryParams } = previousUrlValue;
      const queryParamsStr = new URLSearchParams(queryParams).toString();
      prevUrl = queryParamsStr ? `${url}?${queryParamsStr}` : url;
    }

    const logoutOpts = { errorMessage: INACTIVITY_ERR_MESSAGE, lastKnownUrl, prevUrl };
    this.auth.logout(logoutOpts);
  }
}
